import React, { useEffect, useState } from 'react'
import './totalIncentive.css'
import { Button, Fade, Menu, MenuItem } from '@mui/material'
import FilterAltOutlinedIcon from '@mui/icons-material/FilterAltOutlined'
import DownloadIcon from '@mui/icons-material/Download'
import { utils, writeFile } from 'xlsx-js-style'
import dayjs from 'dayjs'
import { currencyCode } from '../../../services/constant'
import CurrencyCellRenderer from '../../currency-cell-render/currency-cell-render'
import AgGridContainer from '../../../componant/gridContainer/gridContainer'
import CustomDatePicker from '../../../widgets/datePicker/datePicker'
import { SnackbarService } from '../../../services/snackBehaviorSubject/snackBehaviorSubject'
import 'ag-grid-community/styles/ag-grid.css'
import 'ag-grid-community/styles/ag-theme-alpine.css'
import { getTotalIncentiveData } from '../../../api-services/employee-services'

interface DateRangeProps {
  startDate: string
  endDate: string
}

interface TotalIncentiveRow {
  employeeId: string
  employeeName: string
  totalCount: number
  totalIncentive: number
}

const dateRangeField: { name: keyof DateRangeProps, label: string }[] = [
  { name: 'startDate', label: 'From Date' },
  { name: 'endDate', label: 'To Date' }
]

const TotalIncentive = () => {
  const snackbarService = SnackbarService()
  const currency = currencyCode()

  const [selectedDateRange, setSelectedDateRange] = useState<DateRangeProps>({
    startDate: '',
    endDate: ''
  })
  const [rowData, setRowData] = useState<TotalIncentiveRow[]>([])

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)
  const openFilterModal = Boolean(anchorEl)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)
  const [showFilter, setFilter] = useState(false)

  const fetchTotalIncentiveData = () => {
    getTotalIncentiveData(selectedDateRange.startDate, selectedDateRange.endDate).then((response: any) => {
      if (response.status === 200) {
        setRowData(response.data)
      } else {
        snackbarService.next({ data: { open: true, severity: 'error', message: 'Failed to fetch total incentive data' } })
      }
    }).catch((error) => {
      snackbarService.next({ data: { open: true, severity: 'error', message: error.message } })
    })
  }

  useEffect(() => {
    if (selectedDateRange.startDate !== '' && selectedDateRange.endDate !== '') {
      fetchTotalIncentiveData()
      handleCloseFilter()
    }
  }, [selectedDateRange])

  const handleDate = (date: string, name: string) => {
    setSelectedDateRange((prev) => {
      const updated = { ...prev, [name]: date }
      if (updated.startDate && updated.endDate && new Date(updated.endDate) < new Date(updated.startDate)) {
        updated.endDate = ''
      }
      return updated
    })
  }

  const handleOpenFilter = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
  }

  const handleCloseFilter = () => {
    setAnchorEl(null)
  }

  const handleScreenSize = () => {
    const width = window.innerWidth
    if (width !== screenWidth) {
      setScreenWidth(width)
    }
  }

  useEffect(() => {
    window.addEventListener('resize', handleScreenSize)
  }, [])

  useEffect(() => {
    if (screenWidth < 460 || (screenWidth >= 600 && screenWidth <= 773)) {
      setFilter(true)
    } else {
      setFilter(false)
      setAnchorEl(null)
    }
    window.addEventListener('resize', handleScreenSize)
  }, [screenWidth])

  const columnDefs = [
    {
      headerName: 'Employee Id',
      field: 'employeeId',
      minWidth: 150,
      flex: 1,
      resizable: true,
      filter: true
    },
    {
      headerName: 'Employee Name',
      field: 'employeeName',
      minWidth: 200,
      flex: 1,
      resizable: true,
      filter: true
    },
    {
      headerName: 'Total Count',
      field: 'totalCount',
      minWidth: 150,
      flex: 1,
      resizable: true,
      filter: true
    },
    {
      headerName: 'Total Incentive Amount',
      field: 'totalIncentive',
      minWidth: 200,
      flex: 1,
      resizable: true,
      filter: true,
      cellRenderer: (params: any) => (
        <CurrencyCellRenderer value={params.value} currencyCode={currency} />
      )
    }
  ]

  const handleDownloadExcel = () => {
    if (rowData.length === 0) {
      snackbarService.next({ data: { open: true, severity: 'error', message: 'Data not available' } })
      return
    }
    const header = ['Employee Id', 'Employee Name', 'Total Count', 'Total Incentive Amount']
    const formatCurrency = (value: number) => new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency
    }).format(value)
    const data = rowData.map((row) => ([
      row.employeeId,
      row.employeeName,
      row.totalCount,
      formatCurrency(row.totalIncentive)
    ]))
    const ws = utils.aoa_to_sheet([header, ...data])

    header.forEach((_, colIndex) => {
      const cellRef = utils.encode_cell({ r: 0, c: colIndex })
      if (ws[cellRef]) {
        ws[cellRef].s = {
          font: { bold: true }
        }
      }
    })

    ws['!cols'] = header.map((headerText, colIndex) => {
      const maxDataLength = data.reduce((max, row) => {
        const value = row[colIndex]
        const length = value !== undefined && value !== null ? String(value).length : 0
        return Math.max(max, length)
      }, headerText.length)
      return { wch: maxDataLength + 2 }
    })

    const wb = utils.book_new()
    utils.book_append_sheet(wb, ws, 'Total Incentive')
    writeFile(wb, `Total Incentive_${selectedDateRange.startDate}_to_${selectedDateRange.endDate}.xlsx`)
  }

  return (
    <div className='main'>
      <div className='main-container'>
        <div className='heading heading-center-space-between height-40'>
          <h2 className='single-line-text'>Total Incentive</h2>
          <div className='header-sub-container'>
            <div className='date-range-sub-container'>
              {
                !showFilter ? dateRangeField.map((item, index: number) => (
                  <div key={index} className='date-sub-container'>
                    <CustomDatePicker
                      label={item.label}
                      name={item.name}
                      selectedDate={selectedDateRange}
                      handleDate={handleDate}
                      minDate={item.name === 'endDate' ? selectedDateRange.startDate : undefined}
                    />
                  </div>
                )) :
                  <div style={{ display: 'flex' }}
                    onClick={handleOpenFilter}
                    aria-controls={openFilterModal ? 'fade-menu' : undefined}
                    aria-haspopup="true"
                    aria-expanded={openFilterModal ? 'true' : undefined}
                  >
                    <FilterAltOutlinedIcon style={{ color: 'var(--primaryColor)' }} />
                    <label style={{ fontSize: '10px', marginTop: '9px', color: 'var(--primaryColor)', fontWeight: 'bold' }}>Filter</label>
                  </div>
              }

              <Menu
                id="fade-menu"
                MenuListProps={{
                  'aria-labelledby': 'fade-button',
                }}
                anchorEl={anchorEl}
                open={openFilterModal}
                onClose={handleCloseFilter}
                TransitionComponent={Fade}
              >
                {
                  dateRangeField.map((item, index: number) => (
                    <MenuItem key={index} className='menu-item-container'>
                      <div className='date-picker-container'>
                        <CustomDatePicker
                          label={item.label}
                          name={item.name}
                          selectedDate={selectedDateRange}
                          handleDate={handleDate}
                          minDate={item.name === 'endDate' ? selectedDateRange.startDate : undefined}
                        />
                      </div>
                    </MenuItem>
                  ))
                }
              </Menu>
            </div>
          </div>
        </div>
        <div className='cal-40'>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            {selectedDateRange.startDate && selectedDateRange.endDate && <h4 className='total-incentive-subheading'>
              {
                `Total Incentive from ${dayjs(selectedDateRange.startDate).format('DD/MM/YYYY')} to ${dayjs(selectedDateRange.endDate).format('DD/MM/YYYY')}`

              }
            </h4>}
            <div className='total-incentive-table-header'>
              <Button
                variant='outlined'
                className='total-incentive-download-button'
                startIcon={<DownloadIcon />}
                onClick={handleDownloadExcel}
              >
                Download Excel
              </Button>
            </div>
            <div className='ag-grid-container' style={{ flex: 1 }}>
              <AgGridContainer rowData={rowData} columnDefs={columnDefs} />
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default TotalIncentive
